/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { Component } from 'react';
import TagManager from 'react-gtm-module';
import { Switch, Route, Redirect } from 'react-router';
import { ConnectedRouter } from 'connected-react-router';
// pages
import { LoadState } from '@insticator/insticator-ui'; // iui-material
import { Error } from '@insticator/insticator-ui-classic'
import Auth from 'pages/auth/auth_container';
import Dashboard from 'pages/dashboard/dashboard_container';
// material
import { Modal, MessageHolder, OnInActivityMessage } from '@insticator/insticator-ui'; // iui-material
import {
    pageurl, device,
    language, languages, cookieEnabled, userAgent, visitorGeo, isMobile,
    isEmptyObject,
    environment,
    clearAutoLogoutTimer, clearCheckInactivityTimer, setAutoLogoutTimer, setCheckInactivityTimer,
    history
} from '@insticator/insticator-ui'; // iui-utils
// styles
import injectSheet from 'react-jss';
import styles from 'app_styles';



/* ========== ~~~~~~~~~~ APP ~~~~~~~~~~ ========== */
class App extends Component {

    constructor(props) {
        super(props);
        this.state = { phase: 'loading', acceptingAgreement: false, signingIn: false };
        this.authRef = this.props.queryParams['auth_ref'];
        this.tokenUUID = !!this.authRef && !!this.props.queryParams['token_uuid'] ? this.props.queryParams['token_uuid'] : null;
        this.tagManagerArgs = { gtmId: environment === 'PRODUCTION' ? 'GTM-P972HDC' : 'GTM-T77J246' };
    }

    componentDidMount() {
        this.ensurePermissionsAndSitesList();
        this.props.getUserIP();
        this.props.setSessionData({ language, languages, cookieEnabled, userAgent, pageurl, device, visitorGeo, isMobile });

        // Called every time user reloads the application (this causes session extension in BE). This will extend the session timeout check for 23 hours and 30 mins.
        if (!!this.props.isSignedIn) this.setSessionExtensionTimeout(84600000);

        if (!!this.tokenUUID) this.props.setAuthTokenData({ tokenUUID: this.tokenUUID });

        // change class for intro animation (header/sidebar)
        setTimeout(() => { this.setState({ phase: 'loaded'}); }, 100);

        // Initialize Google Tag Manager
        if (environment !== 'LOCALHOST') TagManager.initialize(this.tagManagerArgs)

        // Initialize heap
        this.initializeHeapScript();
        
        // fix feedback form submmitted message not visible on firefox
        this.setFeedbackFormMinHeight();
    }

    setFeedbackFormMinHeight = () => {
      const cssEl = document.createElement('style')
      cssEl.type = 'text/css'
      cssEl.id = 'hbspt-form-style'
      cssEl.textContent = `
        .hubspotForm > iframe, #hs-form-iframe-0 { 
          min-height: 40px !important; 
        }
      `
      document.head.appendChild(cssEl)
    }

    initializeHeapScript = () => {
        const heapId = environment === 'PRODUCTION' ? 3324975950 : 3686026769;
        const heapScript = `window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
        heap.load("${heapId}");`;

        const script = document.createElement('script');
        script.text = heapScript;
        document.head.appendChild(script);
    }

    setSessionExtensionTimeout(timeout) {
        clearCheckInactivityTimer();
        setCheckInactivityTimer(this.handleSessionExtensionCallback, timeout);
    }

    componentDidUpdate(prevProps){
        if (history.location.pathname.replace('/','') !== this.props.currentPage) {
            this.props.updatePageDetails(history.location.pathname.replace('/',''))
        }
        if (prevProps.permissions.length != this.props.permissions.length) {
            this.setPaymentInformation();
        }
        // call the function on signin
        if (!!this.props.isSignedIn && prevProps.isSignedIn !== this.props.isSignedIn) {
            this.setSessionExtensionTimeout(84600000);
            this.ensurePermissionsAndSitesList();
            this.setState({signingIn: true});
        }
    }

    // Gets called when user is signed in and the session is about to expire.
    handleSessionExtensionCallback = () => {
        // get current timestamp
        const currentTime = new Date().getTime();
        // gets the timestamp for last action triggered by user.
        const lastActionDispatchedAt = this.props.lastActionDispatchedAt;
        // calculates time difference between current time and the last time user triggered some action.
        const minutesTolastActionDispatchedAt = Math.ceil(((currentTime - lastActionDispatchedAt) / 1000) / 60);

        // if last time user was active is 15 minutes or more than 15 minutes
        if (minutesTolastActionDispatchedAt >= 15) {
            // auto logout in 30 minutes if no response from user
            setAutoLogoutTimer(this.handleAutoLogout, 1800000);
            // show the modal to confirm if user is still there for 30 minutes
            this.props.displayModal(true, <OnInActivityMessage primaryAction={() => {this.props.extendSession().then(() => this.handleExtendSessionSuccess()).catch(() => this.handleExtendSessionFailure());}}/>, false);
        } else {
            // if the user is active/was recently active on the browser, auto extend the session by 24 hours more. Reset the session extension timeout.
            this.props.extendSession().then(() => this.handleExtendSessionSuccess()).catch(() => this.handleExtendSessionFailure());

        }
    };

    handleAutoLogout = () => !!this.props.mocked ? this.props.unmockUser() : this.props.signOut();

    handleExtendSessionSuccess() {
        clearAutoLogoutTimer();
        this.setSessionExtensionTimeout(84600000);
        this.props.displayModal(false);
    };

    handleExtendSessionFailure() {
        this.props.displayErrorMessage('Sorry! We were unable to extend your session.');
        this.props.displayModal(false);
    }

    userDoesNotHavePermissions = () => this.props.permissions.length < 1;

    userDoesNotHaveSites = () => isEmptyObject(this.props.sites);

    ensurePermissionsAndSitesList() {
        const {
            isSignedIn, accountUUID, siteUUID, userUUID,
            isCommentingDataReceived, mockupGenerator
        } = this.props;

        if (!isSignedIn) return null;
        if (this.userDoesNotHavePermissions()) this.props.getUserPermissions(userUUID);
        if (this.userDoesNotHaveSites()) this.props.getAllSites(accountUUID);
        if (!isCommentingDataReceived && !mockupGenerator) this.props.getCommentingData(accountUUID);
        if (siteUUID && mockupGenerator) this.props.checkForCommentingUnit(siteUUID);
        if (accountUUID) this.props.checkForRatingsUnit(accountUUID);
        if (accountUUID) this.props.getShowPubPortalV2Button(accountUUID);
    }

    setPaymentInformation = () => {
        if (!this.props.mocked && this.props.permissions.includes('settings.paymentInfo')) {
            this.props.getPaymentInformation(this.props.accountUUID);
        }
    };

    // Navigates the user to the appropriate part of the application based on query parameters or cookies.
    // This method runs on page load / refresh.
    navigateUserOnPageLoad = () => {
        // If the URL has query parameters, navigate the user based on them. Otherwise, navigate based on the cookies.
        const path = this.authRef ? this.getPathByQueryParameters() : this.getPathByCookies();
        return <Redirect to={path} />;
    };

    getPathByQueryParameters = () => {
        switch (this.authRef) {
            case 'RESET_PASSWORD':
                if(this.props.isSignedIn) return '/dashboard/home';
                return '/auth/completepasswordreset';
            case 'failed':
                // fall through
            case 'FAILED':
                const googleLoginFailed = `We could not log you in using the Google account you selected; 
                                           please try again using a different account.`;
                this.props.displayErrorMessage(googleLoginFailed);
                return '/auth/signin';
            default:
                return ''
        }
    };

    getPathByCookies = () => {
        // These properties are set to the values of cookies in the sessionStatus reducer on the page load.
        const { isSignedIn, showTermsAndConditions, showSetPassword, mockupGenerator, productCategory } = this.props;

        if (!isSignedIn) {
            return '/auth/signin';
        } else if (showSetPassword) {
            return '/auth/completesignup';
        } else if (showTermsAndConditions) {
            return '/auth/acceptagreement';
        } else if (mockupGenerator && productCategory === 'COMMENTING') {
            return '/dashboard/commenting';
        } else if (mockupGenerator) {
            return '/dashboard/embeds';
        } else {
            if(!this.state.signingIn) return '/dashboard/home';
        }
    };

    _renderLoadState = () => <LoadState graphicName={`insticator-icon`}
                                        graphicText={`Browser with Search Icon`}
                                        bodycopy={`Insticator is loading your data.`}
    />;

    render() {
        const { isSignedIn, authFail, showTermsAndConditions, showSetPassword } = this.props;
        // if (isSignedIn && this.userDoesNotHavePermissions() && !authFail) return this._renderLoadState();
        return (
            <ConnectedRouter history={history}>
                <div id={`app`} className={`${this.props.classes.app}`}>
                    <Switch>
                        <Route exact path='/' render={this.navigateUserOnPageLoad} />
                        <Route path='/auth' render={() => (<Auth />)} />
                        {/* !!! Nested routes to sections are in the Dashboard component !!! */}
                        <PrivateRoute path='/dashboard'
                                      authed={isSignedIn}
                                      component={Dashboard}
                        />
                        <PrivateRoute path='/error'
                                      authed={isSignedIn}
                                      component={Error}
                        />
                        <Redirect to='/error/404' />
                    </Switch>
                    {/*<PrivateRoute authed={props.isDataReceived} exact path='/getstarted' component={GetStartedPage} />*/}
                    <Modal /> {/* activated via redux action/state and actual content is passed through there as well */}
                    <MessageHolder /> {/* activated via redux action/state and actual content is passed through there as well */}
                </div>
            </ConnectedRouter>
        );
    }
}


const PrivateRoute = ({ component: Component, authed, ...rest }) => {
    return(
        <Route
            {...rest}
            render={(props) => authed === true
                ? <Component {...props} />
                : <Redirect to={{ pathname: '/auth/signin', state: { from: props.location } }} />}
        />
    )
};



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(App);

